import React, { useState } from "react";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import { Input } from "./ui/Input";
import PlayerPhotoUpload from "./PlayerPhotoUpload";
import { apiService } from "../services/api";
import toast from "react-hot-toast";
import {
  PencilIcon,
  TrashIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";

const PlayerList = ({ players, onUpdate, onDelete }) => {
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setEditForm({
      name: player.name,
      number: player.number,
      position: player.position || "",
      grade: player.grade || "",
      email: player.email || "",
      parent_emails: (player.parent_emails || []).join("\n"),
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const parentEmails = editForm.parent_emails
        ? editForm.parent_emails
            .split(/[\n,]+/)
            .map((e) => e.trim())
            .filter((e) => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
        : [];

      const { parent_emails: _pe, ...rest } = editForm;
      await apiService.updatePlayer(editingPlayer.id, {
        ...rest,
        number: parseInt(editForm.number),
        grade: editForm.grade ? parseInt(editForm.grade) : null,
        parent_emails: parentEmails.length > 0 ? parentEmails : null,
      });

      setEditingPlayer(null);
      onUpdate();
      toast.success("Player updated successfully!");
    } catch (error) {
      toast.error("Failed to update player");
    }

    setSaving(false);
  };

  const handleCancelEdit = () => {
    setEditingPlayer(null);
    setEditForm({});
  };

  const handleInputChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (players.length === 0) {
    return (
      <div className="text-center py-12">
        <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No players found
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Try adjusting your search or import players from a CSV file.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Player
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Position
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Grade
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Parent Emails
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {players.map((player) => (
              <tr key={player.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      {player.photo_url ? (
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={player.photo_url}
                          alt={player.name}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {player.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {player.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  #{player.number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {player.position || "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {player.grade || "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {player.email || "-"}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {player.parent_emails && player.parent_emails.length > 0
                    ? player.parent_emails.length === 1
                      ? player.parent_emails[0]
                      : `${player.parent_emails[0]} (+${player.parent_emails.length - 1})`
                    : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(player)}
                      title="Edit player"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => onDelete(player.id)}
                      title="Delete player"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Player Modal */}
      <Modal
        isOpen={!!editingPlayer}
        onClose={handleCancelEdit}
        title="Edit Player"
      >
        <form onSubmit={handleSaveEdit} className="space-y-6">
          {/* Photo Upload Section */}
          <div className="flex justify-center pb-4 border-b border-gray-200">
            <PlayerPhotoUpload
              player={editingPlayer}
              onPhotoUpdated={(updatedPlayer) => {
                setEditingPlayer(updatedPlayer);
                onUpdate(); // Refresh the player list
              }}
            />
          </div>

          <Input
            label="Player Name"
            value={editForm.name || ""}
            onChange={(e) => handleInputChange("name", e.target.value)}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Jersey Number"
              type="number"
              min="1"
              max="99"
              value={editForm.number || ""}
              onChange={(e) => handleInputChange("number", e.target.value)}
              required
            />
            <Input
              label="Position"
              placeholder="e.g. Midfielder, Attack, Defense"
              value={editForm.position || ""}
              onChange={(e) => handleInputChange("position", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Grade"
              type="number"
              min="9"
              max="12"
              placeholder="9-12"
              value={editForm.grade || ""}
              onChange={(e) => handleInputChange("grade", e.target.value)}
            />
            <Input
              label="Email"
              type="email"
              placeholder="player@email.com"
              value={editForm.email || ""}
              onChange={(e) => handleInputChange("email", e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parent/Guardian Emails
            </label>
            <textarea
              rows={3}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-sm"
              placeholder="One email per line"
              value={editForm.parent_emails || ""}
              onChange={(e) =>
                handleInputChange("parent_emails", e.target.value)
              }
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter one email per line. These will receive parent report links.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancelEdit}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !editForm.name || !editForm.number}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default PlayerList;

// Alternative simplified version for basic use cases
export const SimplePlayerList = ({ players, onEdit, onDelete }) => {
  return (
    <div className="space-y-2">
      {players.map((player) => (
        <div
          key={player.id}
          className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600">
                {player.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                {player.name}
              </h3>
              <p className="text-sm text-gray-500">
                #{player.number} • {player.position || "No position"}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit?.(player)}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => onDelete?.(player.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Player card component for grid layouts
export const PlayerCard = ({ player, onEdit, onDelete, className = "" }) => {
  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow ${className}`}
    >
      <div className="flex items-center space-x-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
          <span className="text-lg font-medium text-gray-600">
            {player.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-medium text-gray-900">{player.name}</h3>
          <p className="text-sm text-gray-500">#{player.number}</p>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Position:</span>
          <span className="font-medium">{player.position || "Not set"}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Grade:</span>
          <span className="font-medium">{player.grade || "Not set"}</span>
        </div>
        {player.email && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Email:</span>
            <span className="font-medium truncate ml-2">{player.email}</span>
          </div>
        )}
      </div>

      <div className="flex space-x-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit?.(player)}
          className="flex-1"
        >
          <PencilIcon className="w-4 h-4 mr-1" />
          Edit
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={() => onDelete?.(player.id)}
          className="flex-1"
        >
          <TrashIcon className="w-4 h-4 mr-1" />
          Delete
        </Button>
      </div>
    </div>
  );
};
